import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Grid } from '@material-ui/core';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import { Task } from '../tasks/Task.interface';
import TaskStatusCard from '../tasks/cards/TaskStatusCard';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import TaskStatusActions from '../tasks/TaskStatusActions';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import RiskDetailCard from './cards/RiskDetailCard';
import { useFindRiskByTask } from './Risk.hook';
import { Risk, BaseRiskCardProps } from './Risk.interface';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';
import { useUserContext } from '../../user/User.context';
import { isSectionEnabled } from '../../common/checkSectionAvailability';
import RiskReferenceCard from './cards/RiskReferenceCard';
import RiskAssessmentCard from './cards/RiskAssessmentCard';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';
import environment from '../../environments';

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
  .status-container {
    margin-top: 5px;
  }
`;

export const RiskDetailPage: React.FC = () => {
  const { hasRole } = useUserContext();
  const { taskId } = useParams<{ taskId?: string }>();
  const [risk, setRisk] = useState<Risk>();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const { data, error: findError, get } = useFindRiskByTask(Number(taskId));
  const riskCardProps: BaseRiskCardProps = {
    risk: risk!,
    onChange: (newRisk: Partial<Risk>) => {
      if (risk) {
        // notify task components to refresh
        risk.task = { ...risk.task };
        setRisk({ ...risk, ...newRisk });
      }
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskChange = (newTask: Partial<Task>) => {
    if (!risk?.task) {
      return;
    }
    Object.assign(risk.task, newTask);
    riskCardProps.onChange?.({});
    riskCardProps.onUpdate?.();
  };

  useEffect(() => {
    get();
  }, [get]);

  useEffect(() => {
    data && setRisk(data);
  }, [data]);

  useEffect(() => {
    setError(findError);
  }, [findError]);
  const { isFieldDisabled } = useTaskEntityFields(risk?.task);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <StyledTitle>
          <h2>{risk && 'RISK-' + risk.id}</h2>
          <div className='status-container'>
            {risk && <TaskStatusLabel task={risk.task} />}
          </div>
        </StyledTitle>
        <PageTemplateHeaderActions flex>
          {risk?.task && (
            <TaskStatusActions
              task={risk.task}
              taskEntity={risk}
              onChange={task => {
                risk.task = task;
                setRisk({ ...risk });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateDashboard>
        {risk && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <RiskDetailCard {...riskCardProps} />
                </Grid>
                <Grid item xs={12}>
                  <RiskAssessmentCard {...riskCardProps} />
                </Grid>
                {risk.task && (
                  <Grid item xs={12}>
                    <TaskAttachmentCard
                      task={risk.task}
                      onError={riskCardProps.onError}
                      onChange={handleTaskChange}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                {risk.task && (
                  <>
                    <Grid item xs={12}>
                      <TaskStatusCard task={risk.task} />
                    </Grid>
                    {risk.task?.children && risk.task.children.length > 0 && (
                      <Grid item xs={12}>
                        <SubTaskListCard
                          task={risk.task}
                          title='Stakeholders'
                          disabled={isFieldDisabled('assignSubTasks')}
                          onError={riskCardProps.onError}
                          onChange={handleTaskChange}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TaskAssignCard task={risk.task} />
                    </Grid>
                    <Grid item xs={12}>
                      <TaskFileListCard
                        task={risk.task}
                        onError={riskCardProps.onError}
                        onChange={handleTaskChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TaskJiraIssueAssignCard
                        compact
                        task={risk.task}
                        entity={{ title: risk.title }}
                        jiraFieldColumns={
                          environment.riskConfig?.visibleJiraFields
                        }
                        onChange={taskJiraIssues => {
                          // update risk Task jira issues on change
                          risk.task.jiraIssues = taskJiraIssues;
                          riskCardProps.onChange?.({ ...risk });
                        }}
                      />
                    </Grid>
                    {isSectionEnabled('Products') && (
                      <>
                        <Grid item xs={12}>
                          <RiskReferenceCard
                            riskId={risk.id}
                            target='user-needs'
                            disabled={isFieldDisabled('assignUserNeeds')}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <RiskReferenceCard
                            riskId={risk.id}
                            target='design-inputs'
                            disabled={isFieldDisabled('assignDesignInputs')}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <RiskReferenceCard
                            riskId={risk.id}
                            target='design-outputs'
                            disabled={isFieldDisabled('assignDesignOutputs')}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
                {hasRole('Delete') && (
                  <Grid item xs={12}>
                    <DeleteEntityCard
                      title='Risk'
                      apiPath={`risks`}
                      entity={risk}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>
      <AppSnackbar
        severity='error'
        open={Boolean(error)}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Risk details updated.
      </AppSnackbar>
    </PageTemplate>
  );
};

export default RiskDetailPage;
